import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, parseISO } from 'date-fns';
import { CheckCircle2, Loader2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { adminButton } from '@/components/admin/ui';
import { useToast } from '@/hooks/use-toast';
import {
  useCompleteJobVisit,
  useJobCompletion,
  useJobCompletionReports,
  useSendCompletionReport,
} from '@/hooks/useJobPhotos';

interface JobCompletionProps {
  jobId: string;
  /** Shown before the live status loads. */
  initialStatus: string;
  clientEmail: string | null;
  /** Assigned cleaner: gets "Mark complete" with a note. */
  canMarkComplete: boolean;
  /** Manager on Team/Business: can send the customer a report. */
  canSendReport: boolean;
  isManager: boolean;
  onCompleted?: () => void;
}

const completeSchema = z.object({
  note: z.string().max(2000, 'Keep the note under 2,000 characters'),
});
type CompleteValues = z.infer<typeof completeSchema>;

const when = (iso: string | null) => (iso ? format(parseISO(iso), 'd MMM, HH:mm') : '');

export function JobCompletion({
  jobId,
  initialStatus,
  clientEmail,
  canMarkComplete,
  canSendReport,
  isManager,
  onCompleted,
}: JobCompletionProps) {
  const { toast } = useToast();
  const { data: completion } = useJobCompletion(jobId);
  const { data: reports = [] } = useJobCompletionReports(jobId, isManager);
  const complete = useCompleteJobVisit(jobId);
  const sendReport = useSendCompletionReport(jobId);
  const [open, setOpen] = useState(false);
  const form = useForm<CompleteValues>({ resolver: zodResolver(completeSchema), defaultValues: { note: '' } });

  const status = completion?.status ?? initialStatus;
  const isCompleted = status === 'completed';
  const isCancelled = status === 'cancelled';
  const lastSent = reports.find((r) => r.status === 'sent');

  const onComplete = form.handleSubmit((values) =>
    complete.mutate(values.note, {
      onSuccess: () => {
        setOpen(false);
        toast({ title: 'Job marked complete', description: 'Nice work. The office can see your checklist and photos.' });
        onCompleted?.();
      },
      onError: (error) =>
        toast({ title: 'Couldn’t mark this job complete', description: (error as Error)?.message, variant: 'destructive' }),
    }),
  );

  const onSend = () =>
    sendReport.mutate(undefined, {
      onSuccess: (result) =>
        toast({ title: 'Report sent', description: result.recipient ? `We emailed ${result.recipient}.` : undefined }),
      onError: (error) =>
        toast({ title: 'Couldn’t send the report', description: (error as Error)?.message, variant: 'destructive' }),
    });

  if (isCancelled) return null;

  return (
    <section data-testid="job-completion" aria-label="Completion" className="space-y-3">
      {isCompleted && (
        <div className="flex items-start gap-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.08] p-3 text-[13px]">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <div className="min-w-0">
            <p className="font-semibold text-foreground">
              Completed{completion?.completed_at ? ` ${when(completion.completed_at)}` : ''}
            </p>
            {completion?.completion_note && (
              <p data-testid="job-completion-note" className="mt-0.5 whitespace-pre-line text-muted-foreground">
                {completion.completion_note}
              </p>
            )}
          </div>
        </div>
      )}

      {canMarkComplete && !isCompleted && !open && (
        <Button
          type="button"
          className="h-12 w-full gap-2 text-[15px] font-semibold"
          onClick={() => setOpen(true)}
          data-testid="job-mark-complete"
        >
          <CheckCircle2 className="h-5 w-5" />
          Mark complete
        </Button>
      )}

      {canMarkComplete && !isCompleted && open && (
        <form onSubmit={onComplete} className="space-y-2 rounded-xl border border-border bg-muted/30 p-3">
          <Label htmlFor={`complete-note-${jobId}`}>Anything to tell the office? (optional)</Label>
          <Textarea
            id={`complete-note-${jobId}`}
            rows={3}
            maxLength={2000}
            placeholder="e.g. All done. Spare key left with the neighbour at no. 12."
            {...form.register('note')}
          />
          {form.formState.errors.note && <p className="text-[12px] text-destructive">{form.formState.errors.note.message}</p>}
          <div className="flex gap-2">
            <Button type="submit" className="h-11 flex-1 gap-2" disabled={complete.isPending}>
              {complete.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Complete job
            </Button>
            <Button type="button" variant="outline" className="h-11" onClick={() => setOpen(false)} disabled={complete.isPending}>
              Not yet
            </Button>
          </div>
        </form>
      )}

      {canSendReport && isCompleted && (
        <div className="flex flex-col gap-2 rounded-xl border border-border p-3 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1 text-[12.5px] text-muted-foreground">
            {lastSent ? (
              <span data-testid="job-report-last-sent">
                Report {lastSent.trigger === 'auto' ? 'sent automatically' : 'sent'} to {lastSent.recipient} on {when(lastSent.sent_at ?? lastSent.created_at)}.
              </span>
            ) : clientEmail ? (
              <span>Send {clientEmail} a completion report with the checklist and photos.</span>
            ) : (
              <span>Add the customer’s email to the job to send them a completion report.</span>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={adminButton.outline}
            onClick={onSend}
            disabled={!clientEmail || sendReport.isPending}
            data-testid="job-send-report"
          >
            {sendReport.isPending ? <Loader2 className="animate-spin" /> : <Mail />}
            {lastSent ? 'Send again' : 'Send report'}
          </Button>
        </div>
      )}
    </section>
  );
}
